import { Injectable } from "@angular/core";
import { ShellService } from "@tylertech/incode-module";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { DataSourceSet, SystemInfo } from "@tylertech/tyler-api-types";
import { from, lastValueFrom, Observable, of } from "rxjs";
import { AuthorizeService } from "./authorizeService.g";
import { StorageService } from "./storageService";

export interface StampedSystemInfo extends SystemInfo
{
    $expires?: number;
}

export const enum Product
{
    Incode10 = "Incode10",
    Incode9 = "Incode9",
    InfiniteVisions = "Infinite Visions",
}

/** @deprecated - SystemInfoService should be able to answer instead of having a custom class that delegates to a JSON object. */
export class SystemInfoExtended implements StampedSystemInfo
{
    readonly $expires?: number;

    constructor(
        private readonly delegated: StampedSystemInfo)
    {
        this.$expires = delegated.$expires;
    }

    get AdditionalProperties(): Record<string, string>
    {
        return this.delegated.AdditionalProperties;
    }

    get ConnectionKeys(): string[]
    {
        return this.delegated.ConnectionKeys;
    }

    get DataSourceSets(): DataSourceSet[]
    {
        return this.delegated.DataSourceSets;
    }

    get Product(): string
    {
        return this.delegated.Product;
    }

    get SystemVersion(): string
    {
        return this.delegated.SystemVersion;
    }

    get IsInTheCloud(): boolean
    {
        return this.delegated.IsInTheCloud;
    }

    /** @deprecated - SystemInfoService should be able to answer this question. */
    get Organization(): string
    {
        return this.getAdditionalProperty("Organization");
    }

    /** @deprecated - SystemInfoService should be able to answer this question. */
    get useTylerId(): boolean
    {
        return this.getAdditionalProperty("IdentificationSystem") === "TylerID";
    }

    /** @deprecated - SystemInfoService should be able to answer this question. */
    IsIncode10(): boolean
    {
        return this.Product === Product.Incode10;
    }

    /** @deprecated - SystemInfoService should be able to answer this question. */
    IsIncode9(): boolean
    {
        return this.Product === Product.Incode9;
    }

    /** @deprecated - SystemInfoService should be able to answer this question. */
    IsSchoolERPPro(): boolean
    {
        return this.Product === Product.InfiniteVisions;
    }

    /** @deprecated - SystemInfoService should be able to answer this question. */
    IsTest(): boolean
    {
        return this.Product === "test";
    }

    /** @deprecated - SystemInfoService should be able to answer this question. */
    CountyReportName(): string
    {
        return this.Organization.split("County")[0].trim();
    }

    private getAdditionalProperty(key: string): string
    {
        if (!this.AdditionalProperties || !this.AdditionalProperties[key])
        {
            return null;
        }

        return this.AdditionalProperties[key];
    }
}

export const enum SystemInfoStorageKeys
{
    CachedSystemInfo = "cachedSystemInfo",
}

@Injectable()
export class SystemInfoService
{
    private readonly log = getLoggerFor("SystemInfoService");
    private _systemInfo: SystemInfoExtended;

    constructor(
        private readonly $shellService: ShellService,
        private readonly $storageService: StorageService,
        private readonly $authorizeService: AuthorizeService
    ) { }

    get systemInfo(): SystemInfoExtended
    {
        return this._systemInfo;
    }

    // TODO(DO-4549) - do the callers really need to know that we are caching anything here? we should rename this.
    /** @deprecated - use `getSystemInfo` instead */
    @LogInvocation(LogLevel.DEBUG, true)
    GetCachedSystemInfo(): Promise<SystemInfoExtended>
    {
        return lastValueFrom(this.getSystemInfo());
    }

    @LogInvocation(LogLevel.DEBUG)
    getSystemInfo(): Observable<SystemInfoExtended>
    {
        if (!this.systemInfo)
        {
            const cachedSystemInfo = this.$storageService.Get<StampedSystemInfo>(SystemInfoStorageKeys.CachedSystemInfo);
            if (!cachedSystemInfo)
            {
                return from(this.fetchSystemInfo());
            }

            this._systemInfo = new SystemInfoExtended(cachedSystemInfo);
        }

        if (this.systemInfo.$expires < Date.now())
        {
            this.clearCache();

            return from(this.fetchSystemInfo());
        }

        return of(this.systemInfo);
    }

    private fetchSystemInfo(): Promise<SystemInfoExtended>
    {
        return (this.$shellService.isEmbedded()
            ? Promise.resolve(JSON.parse(this.$shellService.getSystemInfo()))
            : this.$authorizeService.GetSystemInfo())
            .then((result: StampedSystemInfo) =>
            {
                const expirationDate = new Date();
                expirationDate.setHours(expirationDate.getHours() + 24);

                result.$expires = expirationDate.getTime();
                this.$storageService.Set(SystemInfoStorageKeys.CachedSystemInfo, result);

                this._systemInfo = new SystemInfoExtended(result);

                return this.systemInfo;
            });
    }

    /** @deprecated - Use  `AuthorizeService.IsLicensed(string)` instead. */
    @LogInvocation(LogLevel.DEBUG)
    IsLicensedFor(moduleKey: string): Promise<boolean>
    {
        return this.$authorizeService.IsLicensed(moduleKey);
    }

    @LogInvocation(LogLevel.DEBUG)
    private clearCache(): void
    {
        this.$storageService.Remove(SystemInfoStorageKeys.CachedSystemInfo);
    }
}
